/*
 * File: CustomerSelectionGrid.tsx
 * Project: papa-bear
 * -----
 * Last Modified: 28 Feb 2022 5:17:39 pm
 * -----
 * Copyright (c) 2022 Elegant Software Solutions All rights reserved.
 */

import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';
import { useRollbar } from '@rollbar/react';
import { Col, Row, message } from 'antd';
import { BlockLoader } from 'components/atoms/BlockLoader';
import { CustomerSelectionCard } from 'components/atoms/CustomerSelectionCard';
import { WimsicalError } from 'components/atoms/WimsicalError';
import NoResults from 'components/atoms/noResults/noResults';
import { useFormikContext } from 'formik';
import { InlineStylesModel } from 'models/InlineStylesModel';
import { SalesOrderPayloadModel } from 'models/SalesOrderPayloadModel';
import { FC, useEffect, useMemo, useRef } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { increaseFirstChunkNumber, increaseSecondChunkNumber, setCustomerData } from 'redux/app/app-slice';
import { setCachedCustomerList, setIsLazyFetchLoading } from 'redux/customerCachingSlice/customerCachingSlice';
import { ReduxState } from 'redux/rootReducer';
import { useLazyGetCustomersQuery } from 'redux/services/useBeautifylDayMonsterService';

/* ****************** CSS ****************** */
const styles: InlineStylesModel = {
  loadingRow: {
    width: '100%',
    paddingTop: 10
  },
  skeleton: {
    backgroundColor: 'white',
    padding: '30px 10px',
    height: 98
  },
  infiniteScrollRow: {
    overflowY: 'auto',
    overflowX: 'hidden',
    maxHeight: '100vh',
    width: '100%',
    paddingTop: 10
  },
  moreLoading: {
    marginTop: 10
  },
  noData: {
    marginTop: 50
  }
};

export const CustomerSelectionGrid: FC = () => {
  const {
    values: { customerId: customerAccount, customerName }
  } = useFormikContext<SalesOrderPayloadModel>();

  const { id: salesOrderId } = useParams();
  const isDraftForm = Boolean(salesOrderId);
  const dispatch = useDispatch();

  const { acuityContext, customerData, firstChunkNumber, secondChunkNumber } = useSelector((state: ReduxState) => state.app);
  const { selectedCompanyDataAreaId } = useSelector((state: ReduxState) => state.salesOrders);
  const { cachedCustomerList, isLazyFetchLoading} = useSelector((state: ReduxState) => state.cachedCustomerList);

  const rollbar = useRollbar();


  const infScrollRef = useRef<InfiniteScroll>(null);

  const [getCustomersLazy, { isError, error, isLoading }] = useLazyGetCustomersQuery();



  /* ****************** API/Hooks ****************** */

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        dispatch(setIsLazyFetchLoading(true));
        const response = await getCustomersLazy({
          orderByField: 'customerAccount',
          // TODO: NEEDS UPDATING, see PapaBear REDUXSTATE.salesOrder.selectedCompanyDataAreaId in sales orders and replicate
          dataAreaId: 'MDSI',
          overrideSkipTake: true
        }).unwrap();

        dispatch(setCachedCustomerList(response.data));
        dispatch(setIsLazyFetchLoading(false));
      } catch (err) {
        console.log(err);
        message.error((err as { data: { errorMessage: string } }).data.errorMessage);
        dispatch(setIsLazyFetchLoading(false));
      }
    };

    if (!cachedCustomerList) fetchData();
  }, [cachedCustomerList]);

  useEffect(() => {
    if (cachedCustomerList) dispatch(setCustomerData(cachedCustomerList.slice(0, 100)));
  }, [cachedCustomerList]);

  /* ****************** JSX ****************** */
  const loadingRenderJSX = (
    <Col span={24} xs={24} sm={12} md={8} lg={8} xl={8} xxl={6}>
      <BlockLoader direction="loader loader--slideUp" />
    </Col>
  );

  const customerSelectionCardJSX =
    customerData &&
    customerData
      .filter((customer) => customer.customerAccount !== customerAccount)
      .map((customer) => (
        <Col key={customer.customerAccount} span={24} xs={24} sm={12} md={12} lg={12} xl={12} xxl={8}>
          <CustomerSelectionCard customer={customer} />
        </Col>
      ));

  const selectedCustomerCardJSX = useMemo(
    () => (
      <Col key={customerAccount} span={24} xs={24} sm={12} md={12} lg={12} xl={12} xxl={8}>
        <CustomerSelectionCard customer={{ customerAccount: customerAccount, name: customerName, dataAreaId: selectedCompanyDataAreaId ?? '' }} />
      </Col>
    ),
    [customerAccount, customerName, selectedCompanyDataAreaId ?? '']
  );

  const hasMore = Boolean(cachedCustomerList && customerData && customerData?.length < cachedCustomerList.length);

  /* ****************** Functions ****************** */

  const append = (): void => {
    if (!cachedCustomerList || !customerData || !infScrollRef.current) return;

    dispatch(increaseFirstChunkNumber());
    dispatch(increaseSecondChunkNumber());
    dispatch(setCustomerData([...customerData, ...cachedCustomerList.slice(firstChunkNumber, secondChunkNumber)]));
  };

  if (isError) {
    const queryError = error as FetchBaseQueryError;
  
    rollbar.error(`${queryError.status} - Error fetching customers`, queryError, { selectedCustomerId: acuityContext?.selectedCustomer.id });
  
    return (
      <Row justify="center" style={{ width: '100%' }}>
        <Col span={24}>
          <WimsicalError status={typeof queryError.status === 'number' ? queryError.status : 500} message={''} />
        </Col>
      </Row>
    );
  }

  /* ****************** Render ****************** */
  // For drafts/edits, only show the selected customer card
  if (isDraftForm) {
    return <Row style={{ marginTop: 16, width: '100%' }}>{selectedCustomerCardJSX}</Row>;
  }

  if (isLoading) {
    return (
      <Row gutter={[16, 16]} justify="center" style={styles.loadingRow}>
        {loadingRenderJSX}
      </Row>
    );
  }

  if (isError) {
    const queryError = error as FetchBaseQueryError;

    return (
      <Row justify="center" style={{ width: '100%' }}>
        <Col span={24}>
          <WimsicalError status={typeof queryError.status === 'number' ? queryError.status : 500} message={''} />
        </Col>
      </Row>
    );
  }

  // if no results
  if (!customerData?.length) {
    return (
      <Row style={{ width: '100%' }} justify="center">
        <Col>
          <NoResults errorMessage="No customers found" />
        </Col>
      </Row>
    );
  }
  // if filter resuts loading
  if (isLoading || !customerSelectionCardJSX) {
    return (
      <Row gutter={[16, 16]} justify="center" style={styles.loadingRow}>
        {loadingRenderJSX}
      </Row>
    );
  }

  return (
      <InfiniteScroll style={{ marginTop: 8 }} ref={infScrollRef} initialScrollY={0} loader={false} next={append} scrollableTarget="test" hasMore={hasMore} dataLength={customerData.length}>
        <Row gutter={[16, 16]}>{customerAccount ? [selectedCustomerCardJSX, ...customerSelectionCardJSX] : customerSelectionCardJSX}</Row>
      </InfiniteScroll>
  )
}